// Privacy Policy and Terms of Use text for /privacy and /terms (LegalPage.tsx).
// DRAFT: not reviewed by a lawyer. Edit the text here; LegalPage renders it and builds the table of contents.
// - a string block is a paragraph, { list } is a bulleted list
// - anything in [square brackets] is a placeholder and renders highlighted; fill them in before publishing
// - CONTACT_EMAIL renders as a mailto link wherever it appears

export const CONTACT_EMAIL = 'contact@investi.com.au';
// bump when the text changes; shown in the draft banner and under the title
export const LEGAL_LAST_UPDATED = '26 October 2026';

export type LegalBlock = string | { list: string[] };

export interface LegalSection {
    id: string;          // anchor, e.g. /privacy#contact
    heading: string;
    blocks: LegalBlock[];
}

export interface LegalDocument {
    title: string;
    pageTitle: string;   // browser tab
    summary: string;
    sections: LegalSection[];
}

export const privacyPolicy: LegalDocument = {
    title: 'Privacy Policy',
    pageTitle: 'Privacy Policy · Investi',
    summary: 'How Investi collects, uses, stores and protects personal information, including the investor and shareholder information we handle for the listed companies that use our platform.',
    sections: [
        {
            id: 'who-we-are',
            heading: 'Who we are',
            blocks: [
                'Investi is an investor relations platform operated by [Company legal name] (ABN [ABN]) ("Investi", "we", "us"). Listed companies ("clients") use Investi to manage their share registry insights, publish ASX announcements to their websites, email announcements and updates to investors, and run the investor section of their website.',
                'We are bound by the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs). This policy explains how we handle personal information and how you can contact us about it.',
            ],
        },
        {
            id: 'two-roles',
            heading: 'Our two roles',
            blocks: [
                'We handle personal information in two different ways:',
                { list: [
                    'For our own customers and their staff: we collect and use the information of people who sign in to Investi, order our services or deal with us. We decide how that information is used.',
                    'On behalf of our clients: we process information about each client\'s investors, shareholders and email subscribers so the client can communicate with them. The client decides how that information is used, and we act on the client\'s instructions. See "Investor and shareholder data we handle for clients" below.',
                ] },
            ],
        },
        {
            id: 'what-we-collect',
            heading: 'What we collect',
            blocks: [
                'Depending on how you deal with us, we may collect:',
                { list: [
                    'Account details for client staff who use Investi: name, work email address, company, role and permissions, profile preferences, and a password, which we store only in hashed form.',
                    'Sign-in information: the one-time verification codes we email to you when you sign in, a "trusted device" cookie if you choose to stay signed in on a device, and the dates and times you sign in.',
                    'Ordering and billing details: contact names, email addresses and company details given to us on an order form, and the invoices and payments we record in our accounting software.',
                    'Investor and subscriber details collected on a client\'s behalf: name, email address, subscription status, how consent was given, tags the client applies, and emails opened or links clicked.',
                    'Investor website sign-in details: if you sign in to a client\'s investor website through Investi (with Google, LinkedIn or a one-time code sent by email), your name, email address, the sign-in method you used, when you signed in, and the investor pages and announcements you viewed on that website.',
                    'Questions and comments you submit to a client through their website, and any answers the client publishes.',
                    'Shareholder information from share registries: holder names, registered addresses, holder email addresses where the registry holds them, and holdings and changes in holdings. See below.',
                    'Technical information: IP address, browser type and the date and time of requests, recorded in our server logs.',
                ] },
                'Where practical, you can deal with us without identifying yourself, for example when reading a client\'s public website. You cannot use the Investi platform anonymously, because an account is needed to protect client data.',
            ],
        },
        {
            id: 'how-we-collect',
            heading: 'How we collect it',
            blocks: [
                'We collect personal information directly from you (for example when you sign in, subscribe to a client\'s updates or contact us), from our clients (for example when a client uploads a contact list), from share registry providers that a client has authorised us to connect to, and automatically through cookies and server logs.',
            ],
        },
        {
            id: 'how-we-use-it',
            heading: 'How we use it',
            blocks: [
                'We use personal information to:',
                { list: [
                    'provide, secure and support the Investi platform, including verifying who you are when you sign in;',
                    'send announcement emails, campaigns, welcome emails and subscription confirmations on behalf of our clients;',
                    'produce the reports and dashboards our clients see, such as registry movements, email statistics and investor activity;',
                    'process orders, issue invoices and manage our client relationships;',
                    'respond to questions, support requests and complaints;',
                    'maintain and improve the platform, and keep it secure; and',
                    'meet our legal obligations.',
                ] },
                'We do not sell personal information, and we do not use our clients\' investor or shareholder data for our own marketing.',
            ],
        },
        {
            id: 'investor-data',
            heading: 'Investor and shareholder data we handle for clients',
            blocks: [
                'When a client connects Investi to its share registry provider (for example Automic, Xcend, Computershare, MUFG, Advanced or Boardroom), we retrieve that client\'s register data so the client can see its holders, top holders and holding movements. Each client is responsible for making sure it is allowed to share that data with us and to use it for investor relations.',
                'We only use a client\'s investor and shareholder data to provide Investi to that client. One client cannot see another client\'s data.',
                'Cached copies of register listings are encrypted on disk and deleted after about 7 days. Other records, such as the holding history used for movement reports and subscriber lists, are kept while the client uses Investi and are then [retention period and deletion process to be confirmed].',
                'If you are an investor or shareholder and have a question about how a company uses your information, please contact that company first. We will help them respond.',
            ],
        },
        {
            id: 'email-marketing',
            heading: 'Email marketing and the Spam Act',
            blocks: [
                'Investi sends commercial electronic messages, such as announcement emails and investor updates, on behalf of our clients. These must comply with the Spam Act 2003 (Cth). In particular:',
                { list: [
                    'emails are only sent to people who have consented, whether expressly (for example by subscribing on the client\'s website) or as inferred consent permitted by the Act; clients who upload or add contacts must record how consent was obtained;',
                    'every email identifies the company that sent it and includes a working unsubscribe link;',
                    'unsubscribe requests are processed automatically and promptly, and well within the 5 business days the Act allows;',
                    'once someone unsubscribes, they are only subscribed again after they confirm through a fresh opt-in email.',
                ] },
                'To show clients how their emails perform, we record when emails are delivered, opened and clicked.',
                'We only send our own marketing emails to client staff where we have consent, and you can unsubscribe at any time.',
            ],
        },
        {
            id: 'cookies-analytics',
            heading: 'Cookies and analytics',
            blocks: [
                'Investi uses cookies that are needed for the platform to work: a session cookie to keep you signed in, and a "trusted device" cookie if you choose to skip the email code on a device you use regularly.',
                'On client websites, the Investi widgets set cookies when an investor signs in, so the investor stays signed in and the client can see which investor pages they have viewed.',
                'If a client connects its Google Analytics property, we read aggregate visitor counts for the client\'s website from Google Analytics to show on the client\'s dashboard. These are totals, not information about individual visitors.',
                '[Confirm whether any other analytics or advertising cookies are used on Investi or on investi.com.au.]',
            ],
        },
        {
            id: 'disclosure',
            heading: 'Who we share it with',
            blocks: [
                'We share personal information only as needed to provide Investi, with:',
                { list: [
                    'the client the information relates to;',
                    'service providers who help us run the platform, such as our hosting provider, our email delivery provider, our accounting software (Xero), Google and LinkedIn (if you choose to sign in with them), and an AI service provider (OpenAI) that drafts announcement summaries and email template content from the announcement and template text we send to it;',
                    'professional advisers, and law enforcement or regulators where the law requires it.',
                ] },
                'Our service providers may only use the information to provide their services to us.',
            ],
        },
        {
            id: 'storage-overseas',
            heading: 'Storage and overseas disclosure',
            blocks: [
                'Investi data is stored on servers located in [hosting location, e.g. Australia — to be confirmed] and operated by [hosting provider].',
                'Some of our service providers store or process information outside Australia. These include Google and OpenAI (United States), LinkedIn (United States), Xero ([location to be confirmed]) and our email delivery provider ([provider and location to be confirmed]). Before we disclose personal information overseas, we take reasonable steps under APP 8 to make sure the recipient handles it in a way that is consistent with the APPs.',
            ],
        },
        {
            id: 'security',
            heading: 'How we protect it',
            blocks: [
                'We take reasonable steps to protect personal information from misuse, interference, loss and unauthorised access, modification or disclosure. These include:',
                { list: [
                    'encrypting all traffic between your browser and Investi (HTTPS);',
                    'storing passwords only as hashes, and asking for a one-time code sent by email when you sign in from a new device;',
                    'encrypting subscriber names and email addresses in our database, and encrypting cached share registry data;',
                    'limiting each user to the companies and roles they have been given.',
                ] },
                'If a data breach is likely to cause serious harm, we will notify affected people and the Office of the Australian Information Commissioner (OAIC) as required by the Notifiable Data Breaches scheme, and we will tell the affected client promptly.',
            ],
        },
        {
            id: 'access-correction',
            heading: 'Accessing and correcting your information',
            blocks: [
                'You can ask for access to the personal information we hold about you, or ask us to correct it, by contacting us at contact@investi.com.au. We will respond within 30 days. We may need to confirm your identity first, and in limited cases the Privacy Act allows us to refuse access; if so, we will tell you why.',
                'If your request relates to information we hold on behalf of a client (for example a company\'s shareholder or subscriber records), we will pass it to that client and help them respond. You can unsubscribe from a client\'s emails at any time using the link in any email.',
            ],
        },
        {
            id: 'complaints',
            heading: 'Complaints',
            blocks: [
                'If you have a concern about how we have handled your personal information, please contact us first at contact@investi.com.au. We will acknowledge your complaint promptly and aim to resolve it within 30 days.',
                'If you are not satisfied with our response, you can complain to the Office of the Australian Information Commissioner (OAIC) at www.oaic.gov.au or on 1300 363 992.',
            ],
        },
        {
            id: 'changes',
            heading: 'Changes to this policy',
            blocks: [
                'We may update this policy from time to time. The current version is always on this page, with the date it was last updated.',
            ],
        },
        {
            id: 'contact',
            heading: 'Contact us',
            blocks: [
                { list: [
                    'Privacy Officer, [Company legal name] (ABN [ABN])',
                    'Email: contact@investi.com.au',
                    'Post: [Postal address]',
                ] },
            ],
        },
    ],
};

export const termsOfUse: LegalDocument = {
    title: 'Terms of Use',
    pageTitle: 'Terms of Use · Investi',
    summary: 'The terms that apply when a company uses the Investi investor relations platform and when its staff sign in to it.',
    sections: [
        {
            id: 'about',
            heading: 'About these terms',
            blocks: [
                'These terms are an agreement between [Company legal name] (ABN [ABN]) ("Investi", "we", "us") and the organisation that uses Investi ("you", the "client"). They apply together with any order form or service order you accept. If an order form conflicts with these terms, the order form applies.',
                'If you accept these terms for an organisation, you confirm you are authorised to do so. Each person who signs in to Investi must also follow these terms.',
            ],
        },
        {
            id: 'the-service',
            heading: 'The service',
            blocks: [
                'Investi is a web platform for investor relations. Depending on your plan, it includes share registry insights from your registry provider, publishing ASX and other exchange announcements to your website, investor email campaigns and templates, contact lists, the Website Hub and website widgets, analytics, and reports.',
                'We may improve and change features over time. If we remove a feature that is a material part of what you pay for, we will tell you in advance.',
            ],
        },
        {
            id: 'accounts',
            heading: 'Accounts and sign-in',
            blocks: [
                { list: [
                    'Each user needs their own account. Do not share passwords or the sign-in codes we email to you.',
                    'You are responsible for who you give access to, for the roles you give them, and for removing access when someone leaves.',
                    'Tell us straight away at contact@investi.com.au if you think an account has been compromised.',
                ] },
            ],
        },
        {
            id: 'your-data',
            heading: 'Your data and your responsibilities',
            blocks: [
                'You own the data you put into Investi or ask us to retrieve for you, including your share register, contact lists and content ("Client Data"). You give us permission to use Client Data only to provide and support the service for you.',
                'You are responsible for:',
                { list: [
                    'having the right to share your registry data with us, and authorising us to connect to your share registry provider;',
                    'complying with the Privacy Act 1988 (Cth) for the investor and shareholder information you use through Investi;',
                    'only emailing people who have consented, as required by the Spam Act 2003 (Cth), and keeping a record of how consent was obtained;',
                    'the accuracy and content of the announcements, emails, answers and website content you publish, including your continuous disclosure obligations.',
                ] },
                'We handle personal information in Client Data as described in our Privacy Policy.',
            ],
        },
        {
            id: 'acceptable-use',
            heading: 'Acceptable use',
            blocks: [
                'You must not:',
                { list: [
                    'use Investi to send spam or misleading messages, or anything unlawful;',
                    'try to access another client\'s data, or test, scan or overload the platform without our written permission;',
                    'copy, resell or reverse engineer the platform, except as the law allows;',
                    'upload malicious code or content you do not have the right to use.',
                ] },
                'We may suspend access if we reasonably believe these rules have been broken, and we will tell you why.',
            ],
        },
        {
            id: 'third-parties',
            heading: 'Third-party services and data',
            blocks: [
                'Investi relies on services and data from third parties, including share registry providers, stock exchanges and market data providers, email delivery providers, Google and LinkedIn. We are not responsible for their availability or for errors in the data they provide.',
                'Prices, charts and registry information in Investi are for information only. They may be delayed or incomplete, and they are not financial advice.',
            ],
        },
        {
            id: 'fees',
            heading: 'Fees and payment',
            blocks: [
                'Fees are set out in your order form. Unless it says otherwise, fees are in Australian dollars, exclude GST, and are invoiced [in advance / monthly / annually — to be confirmed]. Invoices are payable within [number] days.',
                'If an invoice is overdue, we may suspend the service after giving you reasonable notice.',
            ],
        },
        {
            id: 'ip',
            heading: 'Intellectual property',
            blocks: [
                'We own the Investi platform, software, widgets and templates we provide, and any improvements to them. While you are a client, we give you a non-exclusive licence to use them for your investor relations. You keep ownership of your Client Data and your branding.',
            ],
        },
        {
            id: 'confidentiality',
            heading: 'Confidentiality',
            blocks: [
                'Each of us will keep the other\'s confidential information confidential and only use it for this agreement. This includes your Client Data, which may include information that has not yet been released to the market.',
            ],
        },
        {
            id: 'availability',
            heading: 'Availability and support',
            blocks: [
                'We aim to keep Investi available at all times, but we do not guarantee that it will be uninterrupted or error-free unless your order form sets a service level. We may carry out maintenance, and we will try to do this outside ASX trading hours.',
                'Support is available by email at contact@investi.com.au.',
            ],
        },
        {
            id: 'liability',
            heading: 'Australian Consumer Law and liability',
            blocks: [
                'Nothing in these terms excludes, restricts or modifies any right or remedy, or any guarantee, that cannot be excluded under the Australian Consumer Law. Where the law allows us to limit our liability for a failure to comply with a guarantee, our liability is limited to supplying the services again or paying the cost of having them supplied again.',
                'To the extent the law allows, we are not liable for indirect or consequential loss, and our total liability under this agreement is limited to [the fees you paid in the 12 months before the claim — to be confirmed].',
            ],
        },
        {
            id: 'termination',
            heading: 'Term and termination',
            blocks: [
                'This agreement continues for the term in your order form and then [renews / continues month to month — to be confirmed] until either of us ends it with [notice period] notice. Either of us may end it immediately if the other seriously breaches it and does not fix the breach within 14 days of being asked to.',
                'When the agreement ends, your access stops. On request made within [number] days, we will give you an export of your contact lists, and we will then delete Client Data as described in our Privacy Policy.',
            ],
        },
        {
            id: 'changes',
            heading: 'Changes to these terms',
            blocks: [
                'We may update these terms. We will give you at least [number] days\' notice of material changes by email or in the platform. If you do not agree to a change, you may end the agreement before it takes effect.',
            ],
        },
        {
            id: 'governing-law',
            heading: 'Governing law',
            blocks: [
                'These terms are governed by the laws of [State or Territory], Australia, and each of us submits to the courts of that State or Territory.',
            ],
        },
        {
            id: 'contact',
            heading: 'Contact us',
            blocks: [
                { list: [
                    '[Company legal name] (ABN [ABN])',
                    'Email: contact@investi.com.au',
                    'Post: [Postal address]',
                ] },
            ],
        },
    ],
};
